/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — LectureVoixHaute UI (M-C2.4 smoke fix — 2026-09-20)
 * ----------------------------------------------------------------------------
 * Source-of-truth : mte-pedagogy/mte-diagnostic-banks-by-target-level-v1.md v2.1 §8
 * Owner : Atelier (M-C2.4)
 *
 * Archetype : "lecture-voix-haute" — flux PARENT/ADULTE (Ana-direct 19/09 ~22:00 fix #2)
 *   - adulte accompagne
 *   - bouton Demarrer chronometre 1:00 → 0:00
 *   - signal sonore (campainha) à t=0
 *   - adulte introduit mots_lus + erreurs
 *   - calcul MTE automatique : mots_corrects/min + taux_erreur
 *
 * DISTINCTION IMPORTANTE :
 *   - LectureVoixHaute = enfant LIT, adulte OBSERVE/SAISIT (cette UI)
 *   - Dictee            = Sage PARLE, enfant ÉCRIT
 *
 * evalMode : always 'HUMAN' — parent saisit mots_lus + erreurs après lecture.
 *
 * Props (canonical, see lib/banque-types.ts):
 *   - item          : BanqueItemLectureVoixHaute
 *   - onAnswer      : (correct: boolean, payload?: unknown) => void
 *   - disabled?     : boolean
 *   - showFeedback? : 'idle' | 'correct' | 'incorrect'
 *
 * Payload returned via onAnswer :
 *   { motsLus: number, erreurs: number, motsCorrectsPerMin: number, tauxErreurPct: number, durationSec: number }
 * ========================================================================== */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { BanqueItemLectureVoixHaute } from '../../../../lib/banque-types';

export interface LectureVoixHauteProps {
  item: BanqueItemLectureVoixHaute;
  onAnswer: (correct: boolean, payload?: unknown) => void;
  disabled?: boolean;
  showFeedback?: 'idle' | 'correct' | 'incorrect';
}

type PhaseChrono = 'idle' | 'running' | 'finished';

function computeMteFluenceStats(motsLus: number, erreurs: number) {
  const motsCorrects = Math.max(0, motsLus - erreurs);
  const motsCorrectsPerMin = motsCorrects;
  const tauxErreurPct = motsLus > 0 ? Math.round((erreurs / motsLus) * 1000) / 10 : 0;
  return { motsCorrects, motsCorrectsPerMin, tauxErreurPct };
}

function splitFluenceIntoLines(text: string, wordsPerLine: number = 10): Array<{ line: string; cumWordCount: number }> {
  if (!text) return [];
  // Preferred: if text has explicit line breaks (\n), use them as lines — matches
  // traditional printed fluence text format (parent/adulte reads line by line).
  const explicitLines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  if (explicitLines.length > 1) {
    let cumCount = 0;
    return explicitLines.map((line) => {
      const wordCount = line.split(/\s+/u).filter(Boolean).length;
      cumCount += wordCount;
      return { line, cumWordCount: cumCount };
    });
  }
  // Fallback: no explicit newlines → group into ~wordsPerLine-word chunks so the
  // parent can spot the last line / word read at the sonnerie without recounting.
  const words = text.replace(/\s+/g, ' ').trim().split(/\s+/u).filter(Boolean);
  const lines: Array<{ line: string; cumWordCount: number }> = [];
  let cumCount = 0;
  for (let i = 0; i < words.length; i += wordsPerLine) {
    const chunk = words.slice(i, i + wordsPerLine).join(' ');
    const wordCount = Math.min(wordsPerLine, words.length - i);
    cumCount += wordCount;
    lines.push({ line: chunk, cumWordCount: cumCount });
  }
  return lines;
}

function isEvaluationItem(item: any): boolean {
  const usage = item?.meta?.texte_usage;
  return usage === 'evaluation_1_minute';
}

function LectureVoixHaute({
  item,
  onAnswer,
  disabled = false,
  showFeedback = 'idle',
}: LectureVoixHauteProps): JSX.Element {
  const [phase, setPhase] = useState<PhaseChrono>('idle');
  const [secondsLeft, setSecondsLeft] = useState<number>(60);
  const [motsLus, setMotsLus] = useState<string>('');
  const [erreurs, setErreurs] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startChrono = useCallback(() => {
    if (disabled || submitted || phase === 'running') return;
    setPhase('running');
    setSecondsLeft(60);
  }, [disabled, submitted, phase]);

  const restartChrono = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setPhase('idle');
    setSecondsLeft(60);
    setMotsLus('');
    setErreurs('');
    setSubmitted(false);
  }, []);

  // Countdown 1:00 → 0:00 then play campainha
  useEffect(() => {
    if (phase !== 'running') return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          // Campainha audio — Web Audio API beep (no external file)
          try {
            const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'sine';
            o.frequency.value = 880;
            g.gain.value = 0.2;
            o.connect(g).connect(ctx.destination);
            o.start();
            setTimeout(() => { o.stop(); ctx.close(); }, 350);
          } catch (_e) { /* silent */ }
          setPhase('finished');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [phase]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (disabled || submitted) return;
    const ml = parseInt(motsLus, 10);
    const er = parseInt(erreurs, 10);
    if (isNaN(ml) || ml < 0) return;
    if (isNaN(er) || er < 0 || er > ml) return;
    setSubmitted(true);
    const stats = computeMteFluenceStats(ml, er);
    onAnswer(true, {
      motsLus: ml,
      erreurs: er,
      motsCorrectsPerMin: stats.motsCorrectsPerMin,
      tauxErreurPct: stats.tauxErreurPct,
      durationSec: 60,
    });
  };

  const feedbackClass = submitted
    ? showFeedback !== 'idle' ? showFeedback : 'correct'
    : 'idle';

  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  const chronoLabel = `${m}:${s.toString().padStart(2, '0')}`;

  return (
    <form
      className="lecture-voix-haute"
      data-testid="lecture-voix-haute"
      data-archetype="lecture-voix-haute"
      data-item-id={item.id}
      data-eval-mode="HUMAN"
      data-feedback={feedbackClass}
      data-phase={phase}
      onSubmit={handleSubmit}
    >
      <p className="lecture-voix-haute-enonce" aria-label="Énoncé">
        {item.enonce}
      </p>
      {item.consigne && (
        <p className="lecture-voix-haute-consigne">{item.consigne}</p>
      )}
      {isEvaluationItem(item) ? (
                <div className="lecture-voix-haute-text" data-testid="lecture-voix-haute-text" data-evaluation-text="true">
                  {splitFluenceIntoLines(item.textALire).map((entry, idx) => (
                    <p
                      key={idx}
                      className="lecture-voix-haute-line"
                      data-testid={`lecture-voix-haute-line-${idx}`}
                      data-line-index={idx}
                      data-cum-word-count={entry.cumWordCount}
                    >
                      <span className="lecture-voix-haute-line-text">{entry.line}</span>
                      <span className="lecture-voix-haute-line-counter" aria-label={entry.cumWordCount + " mots cumulés"}>
                        {entry.cumWordCount}
                      </span>
                    </p>
                  ))}
                  <p className="lecture-voix-haute-total" data-testid="lecture-voix-haute-total-words">
                    Total : {splitFluenceIntoLines(item.textALire).slice(-1)[0] ? splitFluenceIntoLines(item.textALire).slice(-1)[0].cumWordCount : 0} mots
                  </p>
                </div>
              ) : (
                <p className="lecture-voix-haute-text" data-testid="lecture-voix-haute-text">
                  « {item.textALire} »
                </p>
              )}

      {phase === 'idle' && (
        <div className="lecture-voix-haute-controls" data-testid="lecture-voix-haute-controls">
          <button
            type="button"
            className="lecture-voix-haute-start"
            data-action="start-chrono"
            data-testid="lecture-voix-haute-start"
            onClick={startChrono}
            disabled={disabled || submitted}
          >
            Démarrer le chronomètre (1:00)
          </button>
          <p className="lecture-voix-haute-hint">
            L'adulte lit avec l'enfant · chronomètre 1 min · campainha à la fin · adulte saisit mots lus + erreurs
          </p>
        </div>
      )}

      {phase === 'running' && (
        <div className="lecture-voix-haute-running" data-testid="lecture-voix-haute-running">
          <p className="lecture-voix-haute-chrono" aria-live="polite" data-testid="lecture-voix-haute-chrono">
            {chronoLabel}
          </p>
          <p className="lecture-voix-haute-hint">L'enfant lit à voix haute. L'adulte observe.</p>
          <div className="lecture-voix-haute-running-actions">
            <button
              type="button"
              className="lecture-voix-haute-restart"
              data-action="restart-chrono"
              data-testid="lecture-voix-haute-restart"
              onClick={restartChrono}
              disabled={disabled || submitted}
            >
              Recommencer
            </button>
            <button
              type="button"
              className="lecture-voix-haute-stop"
              data-action="stop-chrono"
              data-testid="lecture-voix-haute-stop"
              onClick={() => {
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                  timerRef.current = null;
                }
                setPhase('finished');
              }}
              disabled={disabled || submitted}
            >
              Terminer maintenant
            </button>
          </div>
        </div>
      )}

      {phase === 'finished' && (
        <div className="lecture-voix-haute-form" data-testid="lecture-voix-haute-form">
          <div className="lecture-voix-haute-form-header">
            <p className="lecture-voix-haute-bell-info" role="status" data-testid="lecture-voix-haute-bell">
              ⏰ Campainha — l'adulte saisit les résultats
            </p>
            <button
              type="button"
              className="lecture-voix-haute-restart-after"
              data-action="restart-lecture"
              data-testid="lecture-voix-haute-restart-after"
              onClick={restartChrono}
              disabled={submitted}
            >
              Recommencer la lecture
            </button>
          </div>
          <label htmlFor={`lvh-motslus-${item.id}`}>
            Mots lus&nbsp;:
            <input
              id={`lvh-motslus-${item.id}`}
              type="number"
              min={0}
              data-testid="lecture-voix-haute-motslus"
              value={motsLus}
              onChange={(e) => setMotsLus(e.target.value)}
              disabled={submitted}
              required
            />
          </label>
          <label htmlFor={`lvh-erreurs-${item.id}`}>
            Erreurs&nbsp;:
            <input
              id={`lvh-erreurs-${item.id}`}
              type="number"
              min={0}
              max={parseInt(motsLus, 10) || undefined}
              data-testid="lecture-voix-haute-erreurs"
              value={erreurs}
              onChange={(e) => setErreurs(e.target.value)}
              disabled={submitted}
              required
            />
          </label>
          <button
            type="submit"
            className="lecture-voix-haute-submit"
            data-action="soumettre"
            data-testid="lecture-voix-haute-submit"
            disabled={submitted || !motsLus || !erreurs}
          >
            Soumettre les résultats
          </button>
        </div>
      )}
    </form>
  );
}

export default LectureVoixHaute;
export { LectureVoixHaute };
